import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { chunk } from 'lodash';

import RowRunner from 'components/RowRunner';
import { MatchTypes } from 'constants/bets';
import { componentsBranding, generalBranding, mobileComponents } from 'constants/branding';
import { AB, POKER_STAR } from 'constants/icons';
import useCellConfigs from 'hooks/useCellConfigs';
import useMarketsPricesVisibleSocketParam from 'hooks/useMarketsPricesVisibleSocketParam';
import { useUpdateEffect } from 'hooks/useUpdateEffect';
import {
  getIsDesktop,
  getIsIframeEnabled,
  getIsMobile,
  getNumberOfMarketSelectionsScroll,
  getOperator
} from 'redux/modules/appConfigs/selectors';
import { getCurrentBetsByType } from 'redux/modules/currentBets/selectors';
import { setMarketScroll } from 'redux/modules/market';
import {
  getMarketPricesFirstKeyLineHandicap,
  getMarketPricesFirstKeyLineId,
  getMarketPricesSecondKeyLineHandicap,
  getMarketPricesSecondKeyLineId
} from 'redux/modules/marketsPrices/selectors';
import { MarketsPricesSocketParamsSections } from 'redux/modules/marketsPrices/type';
import { getMobileEditOfferId } from 'redux/modules/myBets/selectors';
import { PageBlock, PlacementPage } from 'types';
import { IMarket } from 'types/markets';
import { RacingGameTitleClasses } from 'types/raceCard';
import { getLinePlusNumber } from 'utils';
import { getIsSelectionEqualsToKeyLine, getMarketTypes, marketScrollData } from 'utils/market';

import styles from './styles.module.scss';

interface ArrowScrollProps {
  market: IMarket;
  /**
   * Show all runners?
   */
  showAll?: boolean;
  /**
   * Page block for betting
   */
  pageBlock: PageBlock;
  /**
   * Redirect to single market page if inline placement is disabled
   */
  redirectToSingleMarketPage?: boolean;
  /**
   * Always show cells for deep prices, even if market depth is disabled
   */
  showDeepPrices?: boolean;
  /**
   * Whether market depth is enabled
   */
  isDepthEnabled: boolean;
  /**
   * Whether is landscape mode
   */
  isLandscape: boolean;
  /**
   * Used to always show betslip placement for certain markets
   */
  classes?: {
    scrollWrapper?: string;
    runnerRow?: string;
  };
  racingGameTitleClasses?: RacingGameTitleClasses;
  page: PlacementPage;
  mobilePlacementContainerClassName?: string;
  currentSelectionsLine?: number;
  keyLineNumber?: number;
}

const ArrowScroll = ({
  market,
  showAll,
  pageBlock,
  redirectToSingleMarketPage,
  showDeepPrices = false,
  isLandscape,
  isDepthEnabled,
  classes,
  racingGameTitleClasses,
  page,
  mobilePlacementContainerClassName = '',
  currentSelectionsLine = 0,
  keyLineNumber
}: ArrowScrollProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { runners, description } = market;

  const isMobile = useSelector(getIsMobile);
  const isDesktop = useSelector(getIsDesktop);
  const numberOfMarketSelectionsScroll = useSelector(getNumberOfMarketSelectionsScroll);
  const firstKeyLineId = useSelector(getMarketPricesFirstKeyLineId(market.marketId));
  const firstKeyLineHandicap = useSelector(getMarketPricesFirstKeyLineHandicap(market.marketId));
  const secondKeyLineId = useSelector(getMarketPricesSecondKeyLineId(market.marketId));
  const secondKeyLineHandicap = useSelector(getMarketPricesSecondKeyLineHandicap(market.marketId));
  const iframeEnabled = useSelector(getIsIframeEnabled);
  const operator = useSelector(getOperator);
  const unmatchedBets = useSelector(getCurrentBetsByType({ type: MatchTypes.UNMATCHED, ignoreCancelled: true }));
  const mobileEditOfferId = useSelector(getMobileEditOfferId);

  const [displayAllSelections, setDisplayAllSelections] = useState(() =>
    unmatchedBets.some(bet => {
      const runnerIdx = runners.findIndex(runner => runner.selectionId === bet.selectionId);
      return runnerIdx > -1 && runnerIdx >= numberOfMarketSelectionsScroll && mobileEditOfferId === bet.offerId;
    })
  );

  const { desktopCellsContainerWidth, mobileCellsContainerWidth, desktopCellsContainerMinWidth } = useCellConfigs({
    cellGroupsCount: isDepthEnabled ? 3 : 1,
    isMarketView: true,
    showDepth: showDeepPrices,
    addMobileExtraSpace: true
  });

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const keyLineRef = useRef<HTMLDivElement>(null);

  const { ref } = useMarketsPricesVisibleSocketParam({
    marketId: market?.marketId,
    eventId: market?.event?.id,
    section: MarketsPricesSocketParamsSections.SportsMiddleSection,
    observerOptions: { rootMargin: '200px' }
  });

  const { isCombinedTotal, isHandicap, isTotalGoals, isHandicapDoubleLine, isAsianHandicap } = getMarketTypes(
    description.marketType,
    description.bettingType
  );
  const { group, isLongList } = marketScrollData(description.marketType);

  const serializedRunners = useMemo(() => {
    if (firstKeyLineId && secondKeyLineId && !isLongList) {
      const findKeyLineRunners = runners.map(runner => {
        if (
          getIsSelectionEqualsToKeyLine({
            selectionId: runner.selectionId,
            handicap: runner.handicap,
            firstKeyLineId,
            firstKeyLineHandicap,
            secondKeyLineId,
            secondKeyLineHandicap
          })
        ) {
          return { ...runner, keyLine: true };
        }

        return { ...runner, keyLine: false };
      });

      return chunk(findKeyLineRunners, group);
    }

    return chunk(runners, group);
  }, [
    runners,
    firstKeyLineId,
    secondKeyLineId,
    firstKeyLineHandicap,
    secondKeyLineHandicap,
    market.description.marketType,
    isLongList,
    group
  ]);

  const displayShowMoreLessButton = !isHandicapDoubleLine && numberOfMarketSelectionsScroll < runners.length;

  const displayedSelectionLines = useMemo(() => {
    if (isHandicapDoubleLine) {
      if (showAll) {
        return serializedRunners;
      }

      if (currentSelectionsLine === 0) {
        return serializedRunners.slice(0, 3);
      }

      if (currentSelectionsLine === serializedRunners.length - 1) {
        return serializedRunners.slice(currentSelectionsLine - 2, currentSelectionsLine + 1);
      }

      return serializedRunners.slice(currentSelectionsLine - 1, currentSelectionsLine + 2);
    }

    if (displayShowMoreLessButton) {
      if (displayAllSelections) {
        return serializedRunners;
      }

      return serializedRunners.slice(0, numberOfMarketSelectionsScroll);
    }

    return serializedRunners;
  }, [
    serializedRunners,
    showAll,
    currentSelectionsLine,
    displayShowMoreLessButton,
    isHandicapDoubleLine,
    displayAllSelections,
    numberOfMarketSelectionsScroll
  ]);

  const hasScroll = showAll && !(iframeEnabled && (operator === AB || operator === POKER_STAR));

  useUpdateEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      let scrollWidth = container.offsetWidth - container.clientWidth - 4;
      scrollWidth = scrollWidth > 0 ? scrollWidth : 0;

      dispatch(setMarketScroll(scrollWidth));
    }

    if (typeof keyLineNumber === 'number' && keyLineNumber >= 0) {
      if (showAll && keyLineRef.current) {
        const keyLineEl = keyLineRef.current;

        /**
         * Delay scrolling to ensure the element's position has been updated.
         */
        setTimeout(() => {
          keyLineEl.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 0);
      }

      if (isMobile && !showAll && scrollContainerRef.current) {
        scrollContainerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  }, [showAll]);

  useEffect(() => {
    ref(scrollContainerRef.current);
  }, []);

  return (
    <div
      className={classNames({
        'biab_market-combined-total': isCombinedTotal,
        [styles.runnersWrapper__scrollContent]: hasScroll
      })}
      ref={scrollContainerRef}
      data-market-id={market?.marketId}
      data-event-id={market?.event.id}
      data-market-prices={true}
    >
      {!!displayedSelectionLines[0]?.length &&
        displayedSelectionLines.map((groupedRunner, lineIndex) => {
          const isKeyLine = groupedRunner.every(({ keyLine }) => keyLine);
          let isLineSelected = false;

          if (isHandicapDoubleLine) {
            if (showAll) {
              isLineSelected = currentSelectionsLine === lineIndex;
            } else if (currentSelectionsLine === 0) {
              isLineSelected = lineIndex === 0;
            } else if (currentSelectionsLine === serializedRunners.length - 1) {
              isLineSelected = lineIndex === displayedSelectionLines.length - 1;
            } else {
              // The selected line should almost always be in the middle, except 2 cases above
              isLineSelected = lineIndex === 1;
            }
          }

          return (
            <div
              key={`${groupedRunner[0].selectionId}-${lineIndex}`}
              className={classNames({
                runnersWrapper__firstRunner: lineIndex === 0,
                [styles.keyLineMargin]: isHandicapDoubleLine,
                [styles.keyLine]: isKeyLine && isHandicapDoubleLine,
                [styles.keyLine__selected]: isLineSelected,
                [generalBranding.SELECTED_SELECTION_LINE]: isLineSelected,
                [styles.keyLine__mobile]: isHandicapDoubleLine && isMobile,
                [styles.keyLine__desktop]: isHandicapDoubleLine && !isMobile,
                [styles.notKeyLine]: isHandicapDoubleLine && !isKeyLine && !isLineSelected,
                [styles.topPadding]: isHandicapDoubleLine && !isKeyLine
              })}
              ref={keyLineNumber === lineIndex && showAll ? keyLineRef : undefined}
            >
              {isHandicapDoubleLine && (
                <section className={styles.keyLineTop}>
                  {isKeyLine && (
                    <div
                      className={classNames(styles.keyLineLabel, {
                        [styles.keyLineLabel__selected]: isLineSelected,
                        [generalBranding.KEY_LINE_INDICATOR]: isLineSelected,
                        [styles.keyLineLabel__mobile]: isMobile
                      })}
                    >
                      <i className={classNames('fa2 fa2-lightning-16', styles.keyLineLabel__icon)} />
                      <p
                        className={classNames(styles.keyLineLabel__text, {
                          [styles.keyLineLabel__text__mobile]: isMobile
                        })}
                      >
                        {t('market.keyLine')}
                      </p>
                    </div>
                  )}
                  <p
                    className={classNames(styles.runnersWrapper__liner, {
                      [styles.runnersWrapper__liner__mobile]: isMobile
                    })}
                    style={{
                      width: isDesktop ? `calc(${desktopCellsContainerWidth} + 15px)` : mobileCellsContainerWidth
                    }}
                  >
                    {isAsianHandicap && `${t('market.handicap')} `}
                    {getLinePlusNumber(
                      groupedRunner[0].handicap,
                      isHandicap && !isTotalGoals ? groupedRunner[0].handicap > 0 : !isTotalGoals
                    )}
                  </p>
                </section>
              )}
              {groupedRunner.map((runner, runnerIndex) => (
                <RowRunner
                  key={runner.runnerName}
                  market={market}
                  runner={runner}
                  pageBlock={pageBlock}
                  classes={{
                    market: group !== 1 ? styles.runnersWrapper__nonBorder : '',
                    runnerRow: classes?.runnerRow
                  }}
                  redirectToSingleMarketPage={redirectToSingleMarketPage}
                  isLandscape={isLandscape}
                  isDepthEnabled={isDepthEnabled}
                  racingGameTitleClasses={racingGameTitleClasses}
                  page={page}
                  desktopCellsContainerWidth={desktopCellsContainerWidth}
                  mobileCellsContainerWidth={mobileCellsContainerWidth}
                  mobilePlacementContainerClassName={mobilePlacementContainerClassName}
                  desktopCellsContainerMinWidth={desktopCellsContainerMinWidth}
                  noBottomBorder={isHandicapDoubleLine && runnerIndex === groupedRunner.length - 1}
                  isSelectedLine={isLineSelected}
                />
              ))}
            </div>
          );
        })}
      {displayShowMoreLessButton && (
        <button
          className={classNames(styles.showAllLess, {
            [componentsBranding.TEXT_BTN]: !isMobile,
            [mobileComponents.BUTTON]: isMobile,
            [mobileComponents.TEXT]: isMobile
          })}
          onClick={() => setDisplayAllSelections(prevState => !prevState)}
        >
          <i
            className={classNames(styles.showAllLess__icon, {
              'biab_custom-icon-double-chevron-up': displayAllSelections,
              'biab_custom-icon-double-chevron-down': !displayAllSelections
            })}
          />
          {displayAllSelections ? t('market.lines.showLess') : t('market.lines.showAll')}
        </button>
      )}
    </div>
  );
};

export default ArrowScroll;
